import React, { useState } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import procurementDocumentsService from '../../../services/procurementDocumentsService';
import DocumentDownloadButtons from './DocumentDownloadButtons';
import GrnDocumentView from './GrnDocumentView';

const emptyItem = () => ({ description: '', unit: '', qty_ordered: '', qty_received: '', qty_damaged_short: 0, condition: 'Good', remarks: '' });

const CheckField = ({ label, value, onChange }) => (
  <div>
    <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
    <select value={value === null ? '' : String(value)} onChange={(e) => onChange(e.target.value === '' ? null : e.target.value === 'true')} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm">
      <option value="">-</option>
      <option value="true">Yes</option>
      <option value="false">No</option>
    </select>
  </div>
);

const GrnFormModal = ({ purchaseRequest, onClose }) => {
  const [form, setForm] = useState({
    po_number: '',
    date_received: new Date().toISOString().slice(0, 10),
    supplier_vendor_name: purchaseRequest.vendor_name || '',
    delivery_note_invoice_no: '',
    received_by_name: '',
    warehouse_location: '',
    carrier_shipping_method: '',
    qty_matches_po: null,
    quality_acceptable: null,
    packaging_intact: null,
    documents_attached: null,
    overall_status: '',
    follow_up_action: '',
    discrepancy_notes: '',
    inspected_by_name: '',
    warehouse_supervisor_name: '',
  });
  const [items, setItems] = useState([emptyItem()]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(null);
  const [showView, setShowView] = useState(false);

  const set = (field, value) => setForm((p) => ({ ...p, [field]: value }));
  const updateItem = (index, field, value) => {
    setItems((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleSave = async () => {
    if (!items.some((it) => it.description.trim())) {
      toast.error('Add at least one received item');
      return;
    }
    try {
      setSaving(true);
      const res = await procurementDocumentsService.createGrn({
        purchase_request_id: purchaseRequest.id,
        ...form,
        items: items.filter((it) => it.description.trim()),
      });
      setSaved(res.data.data.grn);
      toast.success('GRN created');
    } catch (error) {
      console.error('Create GRN error:', error);
      toast.error(error.response?.data?.message || 'Failed to create GRN');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[60]">
      <div className="relative top-6 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Goods Received Note - {purchaseRequest.request_id}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        {saved ? (
          <div className="text-center py-8">
            <p className="text-green-700 font-medium mb-1">GRN {saved.grn_number} created</p>
            <p className="text-sm text-gray-500 mb-4">Download the document below.</p>
            <div className="flex justify-center">
              <DocumentDownloadButtons
                onView={() => setShowView(true)}
                onDownloadDocx={() => procurementDocumentsService.downloadGrnDocx(saved.id, `${saved.grn_number}.docx`)}
              />
            </div>
            <button onClick={onClose} className="mt-4 text-sm text-gray-500 hover:text-gray-700">
              Close
            </button>
            {showView && <GrnDocumentView grn={saved} onClose={() => setShowView(false)} />}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Date Received</label>
                <input type="date" value={form.date_received} onChange={(e) => set('date_received', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">PO Number</label>
                <input value={form.po_number} onChange={(e) => set('po_number', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Supplier / Vendor Name</label>
                <input value={form.supplier_vendor_name} onChange={(e) => set('supplier_vendor_name', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Delivery Note / Invoice No.</label>
                <input value={form.delivery_note_invoice_no} onChange={(e) => set('delivery_note_invoice_no', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Received By</label>
                <input value={form.received_by_name} onChange={(e) => set('received_by_name', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Warehouse / Location</label>
                <input value={form.warehouse_location} onChange={(e) => set('warehouse_location', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Carrier / Shipping Method</label>
                <input value={form.carrier_shipping_method} onChange={(e) => set('carrier_shipping_method', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
              </div>
            </div>

            <div className="border-t pt-3">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-gray-700">Items Received</h4>
                <button onClick={() => setItems((p) => [...p, emptyItem()])} className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800">
                  <Plus className="h-3.5 w-3.5" /> Add Item
                </button>
              </div>
              <div className="space-y-2">
                {items.map((it, i) => (
                  <div key={i} className="grid grid-cols-12 gap-1.5 items-center">
                    <input placeholder="Description" value={it.description} onChange={(e) => updateItem(i, 'description', e.target.value)} className="col-span-3 px-2 py-1.5 border border-gray-300 rounded text-xs" />
                    <input placeholder="Unit" value={it.unit} onChange={(e) => updateItem(i, 'unit', e.target.value)} className="col-span-1 px-2 py-1.5 border border-gray-300 rounded text-xs" />
                    <input type="number" placeholder="Qty Ord." value={it.qty_ordered} onChange={(e) => updateItem(i, 'qty_ordered', e.target.value)} className="col-span-1 px-2 py-1.5 border border-gray-300 rounded text-xs" />
                    <input type="number" placeholder="Qty Rec." value={it.qty_received} onChange={(e) => updateItem(i, 'qty_received', e.target.value)} className="col-span-1 px-2 py-1.5 border border-gray-300 rounded text-xs" />
                    <input type="number" placeholder="Damaged" value={it.qty_damaged_short} onChange={(e) => updateItem(i, 'qty_damaged_short', e.target.value)} className="col-span-1 px-2 py-1.5 border border-gray-300 rounded text-xs" />
                    <input placeholder="Condition" value={it.condition} onChange={(e) => updateItem(i, 'condition', e.target.value)} className="col-span-2 px-2 py-1.5 border border-gray-300 rounded text-xs" />
                    <input placeholder="Remarks" value={it.remarks} onChange={(e) => updateItem(i, 'remarks', e.target.value)} className="col-span-2 px-2 py-1.5 border border-gray-300 rounded text-xs" />
                    <button onClick={() => setItems((p) => p.filter((_, idx) => idx !== i))} className="col-span-1 flex justify-center text-red-500 hover:text-red-700">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t pt-3">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Inspection Summary</h4>
              <div className="grid grid-cols-4 gap-3">
                <CheckField label="Quantity Matches PO?" value={form.qty_matches_po} onChange={(v) => set('qty_matches_po', v)} />
                <CheckField label="Quality Acceptable?" value={form.quality_acceptable} onChange={(v) => set('quality_acceptable', v)} />
                <CheckField label="Packaging Intact?" value={form.packaging_intact} onChange={(v) => set('packaging_intact', v)} />
                <CheckField label="Documents Attached?" value={form.documents_attached} onChange={(v) => set('documents_attached', v)} />
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Overall Status</label>
                  <select value={form.overall_status} onChange={(e) => set('overall_status', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm">
                    <option value="">-</option>
                    <option value="accepted">Accepted</option>
                    <option value="partially_accepted">Partially Accepted</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Follow-up Action Required</label>
                  <input value={form.follow_up_action} onChange={(e) => set('follow_up_action', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
                </div>
              </div>
              <div className="mt-3">
                <label className="block text-xs font-medium text-gray-700 mb-1">Discrepancy / Damage Notes</label>
                <textarea value={form.discrepancy_notes} onChange={(e) => set('discrepancy_notes', e.target.value)} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <input placeholder="Inspected By" value={form.inspected_by_name} onChange={(e) => set('inspected_by_name', e.target.value)} className="px-3 py-2 border border-gray-300 rounded-md text-sm" />
                <input placeholder="Warehouse Supervisor" value={form.warehouse_supervisor_name} onChange={(e) => set('warehouse_supervisor_name', e.target.value)} className="px-3 py-2 border border-gray-300 rounded-md text-sm" />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t">
              <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 disabled:opacity-50">
                {saving ? 'Saving...' : 'Create GRN'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GrnFormModal;
